import { inject, observer } from 'mobx-react';
import * as React from 'react';
import { IFormHelperProps, ISelectOption, ITextFieldProps, Spinner } from 'telia-front-react';

import { RouteService } from '../../../services/RouteService';
import EInvoiceSuppliersDataStore from '../../../stores/data/EInvoiceSuppliersDataStore';
import { FeatureStore } from '../../../stores/FeatureStore';
import InvoiceSettingsModalStore from '../../../stores/InvoiceSettingsModalStore';
import InvoiceSettingsPanelStore, { IEmailAddressFieldState } from '../../../stores/InvoiceSettingsPanelStore';
import { Address } from '../../../stores/models/Address';
import { ISsoStore } from '../../../stores/SsoStore';
import { ITranslateStore } from '../../../stores/TranslateStore';

import TemplateBillingSettingsPanel from '../../../templates/settings-panel';
import { FileTypeOption } from '../../../stores/models/CustomerInvoicesSettings';
import AddressAutocomplete from './address/AddressAutocomplete';

interface IInvoiceSettingsPanelProps {
  invoiceSettingsPanelStore?: InvoiceSettingsPanelStore;
  invoiceSettingsModalStore?: InvoiceSettingsModalStore;
  eInvoiceSuppliersDataStore?: EInvoiceSuppliersDataStore;
  translateStore?: ITranslateStore;
  ssoStore?: ISsoStore;
  featureStore?: FeatureStore;
  routeService?: RouteService;
  errorNotice?: React.ReactNode;
  hasMainHeader?: boolean;
  multipleDeliveryOptionsSupported?: boolean;
}

@inject('invoiceSettingsPanelStore', 'invoiceSettingsModalStore', 'eInvoiceSuppliersDataStore', 'translateStore', 'ssoStore', 'featureStore', 'routeService')
@observer
export default class InvoiceSettingsPanel extends React.Component<IInvoiceSettingsPanelProps> {
  private mapEmailToTextFieldProps(emailAddress: IEmailAddressFieldState, index: number): ITextFieldProps {
    const translate = this.props.translateStore.translate;
    return {
      id: `invoice-settings-panel-email-textfield-${index}`,
      label: translate('invoices.invoice-settings-modal.recipient-email'),
      value: emailAddress.value || '',
      isInvalid: emailAddress.hasError,
      onChange: (value) => this.props.invoiceSettingsPanelStore.setEmail(index, value),
      helper: emailAddress.hasError
        ? {
            text: translate('invoices.invoice-settings-modal.recipient-email-error'),
            type: 'error'
          }
        : null
    };
  }

  public render(): JSX.Element {
    const translate = this.props.translateStore.translate;
    const store = this.props.invoiceSettingsPanelStore;
    const { invoiceSettings, originalSettings } = store;
    const { eInvoiceSuppliers } = this.props.eInvoiceSuppliersDataStore;

    if (this.props.multipleDeliveryOptionsSupported && eInvoiceSuppliers.isInitialLoading) {
      return <Spinner />;
    }

    if (!invoiceSettings || !originalSettings) {
      return <Spinner />;
    }

    return (
      <TemplateBillingSettingsPanel
        title={translate('invoices.invoice-settings-modal.title')}
        hasMainHeader={this.props.hasMainHeader === null ? true : this.props.hasMainHeader}
        bill={{
          icon: store.icon,
          serviceTitle: store.title || store.originalTitle,
          label: translate('invoices.invoice-settings-modal.invoice-name'),
          billNumber: invoiceSettings.refNumber,
          languageHeading: translate('invoices.invoice-settings-modal.invoice-language'),
          language:
            store.invoiceLanguages.length < 2
              ? null
              : {
                  id: 'invoices.invoice-settings-modal.invoice-language-id',
                  label: '',
                  items: store.invoiceLanguages.map((lang) => {
                    return {
                      id: `invoice-settings-panel-invoice-language-${lang}`,
                      label: translate(`invoices.invoice-settings-modal.invoice-language-${lang}`),
                      name: 'language-selection',
                      value: lang
                    };
                  }),
                  value: invoiceSettings.invoiceLanguage,
                  onChange: (lang) => store.updateLanguage(lang as string)
                },
          billTitle: store.editableBillTitle
            ? {
                label: translate('invoices.invoice-settings-modal.invoice-name'),
                id: 'invoice-settings-panel-bill-title-textfield',
                value: invoiceSettings.title || '',
                helper: {
                  text: translate('invoices.invoice-settings-modal.invoice-name-help'),
                  type: 'help'
                },
                input: { maxLength: 60 },
                onChange: (title) => store.updateTitle(title)
              }
            : null
        }}
        recipientOptions={
          invoiceSettings.isRecipientSameAsCustomer != null
            ? {
                isSameAsCustomerChoice: {
                  id: 'invoice-settings-is-recipient-same-as-customer-choice-id',
                  label: '',
                  type: 'radio',
                  options: ['same-as-customer', 'not-same-as-customer'].map((supplier: string) => {
                    return {
                      id: `invoice-settings-panel-${supplier}`,
                      value: supplier,
                      labelText:
                        supplier === 'same-as-customer' ? this.props.ssoStore.customer.displayName : translate(`invoices.invoice-settings-modal.${supplier}`)
                    };
                  }),
                  value: invoiceSettings.isRecipientSameAsCustomer ? 'same-as-customer' : 'not-same-as-customer',
                  onChange: (value) => store.setRecipientSameAsCustomer(value === 'same-as-customer')
                },
                isSameAsCustomer: store.invoiceSettings.isRecipientSameAsCustomer,
                recipientName: {
                  id: 'invoice-settings-modal-recipient-name-textfield',
                  label: translate('invoices.invoice-settings-modal.recipient-name'),
                  value: invoiceSettings.bankRecipientName || '',
                  isInvalid: store.bankUserNameHasError,
                  onChange: (bankRecipientName) => store.updateInvoiceSettings({ bankRecipientName: bankRecipientName }),
                  helper: store.bankUserNameHasError
                    ? {
                        text: translate('invoices.invoice-settings-modal.bank-user-name-error'),
                        type: 'error'
                      }
                    : null
                },
                recipientAddressComponent: (
                  <AddressAutocomplete
                    label={translate('invoices.invoice-settings-modal.recipient-postal-address')}
                    initialAddress={invoiceSettings.address}
                    onSubmit={(address: Address, room: string) => {
                      store.selectAddress(address, room);
                    }}
                    addressHasError={store.addressHasError}
                    apartmentHasError={store.apartmentHasError}
                  />
                ),
                headingLabel: translate('invoices.invoice-settings-modal.invoice-receiver')
              }
            : null
        }
        deliveryOptions={{
          title: translate('invoices.invoice-settings-modal.delivery-options-title'),
          description: translate('invoices.invoice-settings-modal.delivery-options-description'),
          orderLabel: translate('invoices.invoice-settings-modal.order-label'),
          selfServiceOptionCheck: {
            checked: invoiceSettings.invoiceDeliveries.length === 0,
            id: 'invoice-settings-panel-self-service-check',
            value: 'SELFSERVICE',
            onChange: () => store.clearInvoiceDeliveries(),
            labelText: translate('invoices.invoice-settings-modal.invoice-to-self-service')
          },
          bankOptionCheck: {
            checked: invoiceSettings.invoiceDeliveries.includes('BANK'),
            id: 'invoice-settings-panel-bank-check',
            value: 'BANK',
            onChange: (checked) => (checked ? store.addInvoiceDelivery('BANK') : store.removeInvoiceDelivery('BANK')),
            labelText: translate('invoices.invoice-settings-modal.invoice-to-bank')
          },
          bankOptionInfo: translate('invoices.invoice-settings-modal.invoice-to-bank-help'),
          bankOptionAccountNumber: {
            id: 'invoice-settings-panel-bank-iban-textfield',
            label: translate('invoices.invoice-settings-modal.bank-account-number'),
            value: invoiceSettings.ibanOrUserCode || '',
            isInvalid: store.bankOptionAccountNumberError,
            onChange: (iban) => store.updateIban(iban.replace(/ /g, '')),
            helper: this.getBankOptionAccountNumberHelper()
          },
          bankOptionUserName: {
            id: 'invoice-settings-panel-bank-username-textfield',
            label: translate('invoices.invoice-settings-modal.bank-user-name'),
            value: invoiceSettings.bankRecipientName || '',
            isInvalid: store.bankUserNameHasError,
            onChange: (bankRecipientName) => store.updateInvoiceSettings({ bankRecipientName: bankRecipientName }),
            helper: store.bankUserNameHasError
              ? {
                  text: translate('invoices.invoice-settings-modal.bank-user-name-error'),
                  type: 'error'
                }
              : null
          },
          bankOptionUserCode: {
            id: 'invoice-settings-panel-bank-usercode-textfield',
            label: translate('invoices.invoice-settings-modal.bank-user-code'),
            value: invoiceSettings.bankRecipientCode || '',
            isInvalid: store.bankUserCodeHasError,
            onChange: (bankRecipientCode) => store.updateInvoiceSettings({ bankRecipientCode: bankRecipientCode }),
            helper: store.bankUserCodeHasError
              ? {
                  text: translate('invoices.invoice-settings-modal.bank-user-code-error'),
                  type: 'error'
                }
              : null
          },
          bankOptionDropdown: {
            id: 'invoice-settings-panel-bank-name-dropdown',
            placeholder: translate('invoices.invoice-settings-modal.choose-bank'),
            label: translate('invoices.invoice-settings-modal.choose-bank'),
            options: store.bankNameOptions,
            onChange: (value: ISelectOption) => store.setBankNameOption(value),
            value: { value: invoiceSettings.bankCode, label: '' }
          },
          bankOptionAddress: {
            id: 'invoice-settings-panel-bank-iban-textfield',
            label:
              store.bankAddressType === 'IBAN'
                ? translate('invoices.invoice-settings-modal.bank-account-number')
                : translate('invoices.invoice-settings-modal.bank-user-code'),
            value: this.getBankAddressValue(),
            isInvalid: store.bankOptionAccountNumberError,
            onChange: (bankAddress) => store.updateIban(bankAddress.replace(/ /g, '')),
            helper: this.getBankOptionAccountNumberHelper()
          },
          bankOptionShowCancelPostDelivery: store.showCancelPostDelivery('BANK'),
          emailOptionCheck: {
            checked: invoiceSettings.invoiceDeliveries.includes('EMAIL'),
            id: 'invoice-settings-panel-email-check',
            value: 'EMAIL',
            onChange: (checked) => (checked ? store.addInvoiceDelivery('EMAIL') : store.removeInvoiceDelivery('EMAIL')),
            labelText: translate('invoices.invoice-settings-modal.with-email')
          },
          emailAddresses: store.emailAddresses.map((emailAddress, index) => this.mapEmailToTextFieldProps(emailAddress, index)),
          emailOptionShowCancelPostDelivery: store.showCancelPostDelivery('EMAIL'),
          billFormat:
            store.invoiceFileTypes.length < 2
              ? null
              : {
                  id: 'invoice-settings-panel-file-format-option-id',
                  label: translate('invoices.invoice-settings-modal.invoice-format'),
                  type: 'check',
                  options: store.invoiceFileTypes.map((fileType) => {
                    return {
                      id: `invoice-settings-panel-file-format-option-${fileType}`,
                      value: fileType,
                      checked: store.invoiceSettings.fileTypes.includes(fileType),
                      labelText: translate(`invoices.invoice-settings-modal.invoice-format.${fileType}`)
                    };
                  }),
                  value: invoiceSettings.fileTypes,
                  onChange: (value) => store.setFileType(value as FileTypeOption),
                  helper: store.fileTypesHasError
                    ? {
                        text: translate('invoices.invoice-settings-modal.file-types-error'),
                        type: 'error'
                      }
                    : null
                },
          addEmailButton: store.addEmailAllowed
            ? {
                id: 'invoice-settings-panel-add-email-button',
                text: translate('invoices.invoice-settings-modal.add-another-email'),
                onClick: () => store.addEmail()
              }
            : null,
          paperBillOptionCheck: {
            checked: invoiceSettings.invoiceDeliveries.includes('POST'),
            id: 'invoice-settings-panel-post-check',
            value: 'POST',
            onChange: (checked) => (checked ? store.addInvoiceDelivery('POST') : store.removeInvoiceDelivery('POST')),
            labelText: translate('invoices.invoice-settings-modal.with-post')
          },
          paperBillNotice:
            invoiceSettings.isRecipientSameAsCustomer !== false
              ? translate('invoices.invoice-settings-modal.with-post.notice')
              : translate('invoices.invoice-settings-modal.with-post.recipient-address-is-used'),
          paperBillAddressComponent:
            invoiceSettings.isRecipientSameAsCustomer !== false ? (
              <AddressAutocomplete
                label={translate('invoices.invoice-settings-modal.postal-address')}
                initialAddress={invoiceSettings.address}
                onSubmit={(address: Address, room: string) => {
                  store.selectAddress(address, room);
                }}
                addressHasError={store.addressHasError}
                apartmentHasError={store.apartmentHasError}
              />
            ) : null,
          cancelPostDeliveryCheck: {
            id: 'cancel-post-delivery-check',
            value: 'CANCEL-POST',
            checked: store.isPostDeliveryCancelled,
            onChange: (isCancelled) => store.togglePostDeliveryCancelOption(isCancelled),
            labelText: translate('invoices.invoice-settings-modal.cancel-post.checkbox-label')
          },
          cancelPostDeliveryNoticeText: translate('invoices.invoice-settings-modal.cancel-post.notice'),
          invoiceCentralOptionCheck: {
            checked: invoiceSettings.invoiceDeliveries.includes('INVOICE_CENTRAL'),
            id: 'invoice-settings-panel-invoice-central-check',
            value: 'INVOICE_CENTRAL',
            onChange: (checked) => (checked ? store.addInvoiceDelivery('INVOICE_CENTRAL') : store.removeInvoiceDelivery('INVOICE_CENTRAL')),
            labelText: translate('invoices.invoice-settings-modal.with-invoice-central')
          },
          invoiceCentralSupplier: {
            id: 'invoices.invoice-settings-modal.invoice-central-supplier-choice-id',
            label: translate('invoices.invoice-settings-modal.invoice-central-supplier-choice'),
            type: 'radio',
            options: ['OMNIVA', 'FITEK'].map((supplier: string) => {
              return {
                id: `invoice-central-supplier-${supplier}`,
                value: supplier,
                checked: store.invoiceSettings.invoiceCentralSupplier === supplier,
                labelText: translate(`invoices.invoice-settings.delivery-channels.invoice-central-supplier.${supplier}`)
              };
            }),
            onChange: (supplier: string) => store.setInvoiceCentralSupplier(supplier),
            value: invoiceSettings.invoiceCentralSupplier
          },
          invoiceCentralOptionInfo: translate('invoices.invoice-settings-modal.invoice-to-invoice-central-help'),
          invoiceCentralOptionAddress: {
            id: 'invoice-settings-panel-invoice-central-textfield',
            label:
              store.invoiceCentralAddressType === 'REG_NUM'
                ? translate('invoices.invoice-settings-modal.invoice-central-user-code')
                : translate('invoices.invoice-settings-modal.invoice-central-custom'),
            value: this.getInvoiceCentralAddressValue(),
            isInvalid: store.invoiceCentralOptionAddressInvalid,
            onChange: (invoiceCentralAddress) => store.updateInvoiceCentralAddress(invoiceCentralAddress.replace(/ /g, '')),
            helper: this.getInvoiceCentralOptionAddressHelper()
          },
          invoiceCentralOptionDropdown: {
            id: 'invoice-settings-panel-invoice-central-dropdown',
            placeholder: translate('invoices.invoice-settings-modal.choose-invoice-central'),
            label: translate('invoices.invoice-settings-modal.choose-invoice-central'),
            options: store.invoiceCentralOptions,
            onChange: (value: ISelectOption) => store.setInvoiceCentralOption(value),
            value: { value: invoiceSettings.invoiceCentralCode, label: '' }
          },
          invoiceCentralSupported: store.invoiceCentralSupported,
          invoiceCentralOptionShowCancelPostDelivery: store.showCancelPostDelivery('INVOICE_CENTRAL'),
          multipleDeliveryOptionsSupported: store.multipleDeliveryOptionsSupported,
          mobileOptionCheck: {
            checked: invoiceSettings.invoiceDeliveries.includes('MOBILE'),
            id: 'invoice-settings-panel-mobile-check',
            value: 'MOBILE',
            onChange: (checked) => (checked ? store.addInvoiceDelivery('MOBILE') : store.removeInvoiceDelivery('MOBILE')),
            labelText: translate('invoices.invoice-settings-modal.with-mobile')
          },
          mobileNumber: {
            id: 'invoice-settings-panel-mobile-textfield',
            label: translate('invoices.invoice-settings-modal.recipient-mobile'),
            value: store.mobileNumber.value || '',
            type: 'tel',
            onChange: (mobileNumber) => this.props.invoiceSettingsPanelStore.updateMobile(mobileNumber),
            isInvalid: store.mobileNumber.hasError,
            helper: store.mobileNumber.hasError && {
              text: translate(`invoices.invoice-settings-modal.recipient-mobile-error.${this.props.invoiceSettingsPanelStore.mobileNumberValidationResult}`),
              type: 'error'
            }
          },
          mobileOptionShowCancelPostDelivery: store.showCancelPostDelivery('MOBILE')
        }}
        errorNotice={this.props.invoiceSettingsPanelStore.isSaveFailed ? this.props.errorNotice : null}
      />
    );
  }

  private getBankOptionAccountNumberHelper(): IFormHelperProps {
    const store = this.props.invoiceSettingsPanelStore;
    const translate = this.props.translateStore.translate;

    if (store.bankOptionAccountNumberUnsupported) {
      return {
        text: translate('invoices.invoice-settings-modal.bank-account-number-unsupported-error'),
        type: 'error'
      };
    }
    if (store.bankOptionAccountNumberInvalid) {
      return {
        text: translate('invoices.invoice-settings-modal.bank-account-number-error'),
        type: 'error'
      };
    }
    if (store.bankOptionAccountNumberEInvoiceNotSupported) {
      return {
        text: translate('invoices.invoice-settings-modal.bank-e-invoice-not-supported-error'),
        type: 'error'
      };
    }
    if (store.bankOptionAccountNumberAndBankCodeMismatch) {
      return {
        text: translate('invoices.invoice-settings-modal.bank-code-and-iban-mismatch-error'),
        type: 'error'
      };
    }
    return null;
  }

  private getBankAddressValue() {
    const store = this.props.invoiceSettingsPanelStore;
    const invoiceSettings = store.invoiceSettings;

    if (invoiceSettings.invoiceDeliveries.includes('BANK') && invoiceSettings.bankCode) {
      return invoiceSettings.ibanOrUserCode;
    }
    return '';
  }

  private getInvoiceCentralAddressValue() {
    const store = this.props.invoiceSettingsPanelStore;
    const invoiceSettings = store.invoiceSettings;

    if (invoiceSettings.invoiceDeliveries.includes('INVOICE_CENTRAL') && invoiceSettings.invoiceCentralCode) {
      return invoiceSettings.invoiceCentralAddress;
    } else if (store.invoiceCentralAddressType === 'REG_NUM') {
      return this.props.ssoStore.customer.registrationNumber;
    }
    return '';
  }

  private getInvoiceCentralOptionAddressHelper(): IFormHelperProps {
    const store = this.props.invoiceSettingsPanelStore;
    const translate = this.props.translateStore.translate;

    if (store.invoiceCentralOptionAddressInvalid) {
      return {
        text: translate('invoices.invoice-settings-modal.invoice-central-address-error'),
        type: 'error'
      };
    }
    return null;
  }
}
